import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS as AUTOFILL_COMPATIBLE_BIOENTITY_OPTION_DEFINITIONS,
  AutofillCompatibleBioentityOption,
  AutofillNames,
} from 'metadata/AutofillCompatibleBioentityOptions';
import {trackSubmitAutoFillFeature} from 'molbio/SequenceAnalyticsHelper';
import * as AutoFillFieldActions from 'registry/AutoFillFieldActions';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import throttle from 'util/throttle';

function AutofillInput({
  autofillOptions,
  fileId,
}: {
  autofillOptions: Array<AutofillActions>;
  fileId: string;
}): JSX.Element {
  const handleAutofillAction = (action: AutofillActions): void => {
    trackSubmitAutoFillFeature([fileId], ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE, action.name);
    action.autofillActionFn(fileId);
  };
  const sharedButtonProps: ButtonProps = {
    size: ButtonSize.MINI,
    buttonStyle: ButtonStyle.PRIMARY,
    icon: 'info',
  };

  if (autofillOptions.length === 1) {
    const autofillOption = autofillOptions[0];
    return (
      <Button
        {...sharedButtonProps}
        text={`Autofill ${autofillOption.name.toLowerCase()}`}
        onMouseDown={() => {
          handleAutofillAction(autofillOption);
        }}
        dataTestAttrs={{component: 'AutoFillButton', element: `autofill${autofillOption.name}`}}
      />
    );
  } else {
    return (
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM}>
            {autofillOptions.map((autofillOption) => (
              <Option
                key={autofillOption.name}
                onSelect={() => {
                  handleAutofillAction(autofillOption);
                }}
              >
                {autofillOption.name}
              </Option>
            ))}
          </Menu>
        }
      >
        <Button {...sharedButtonProps} text="Autofill" isDropdown={true} />
      </Dropdown>
    );
  }
}

interface AutofillActions extends AutofillCompatibleBioentityOption {
  // Due to throttling this is a nested promise, but we don't actually await on it anyway since autofilling
  // can take upwards of several minutes.
  autofillActionFn: (sequenceId: string) => Promise<Promise<void>>;
}

const THROTTLE_INTERVAL = 1000;

const THROTTLED_AUTOFILL_ACTIONS: {[name in AutofillNames]: (sequenceId: string) => Promise<Promise<void>>} =
  {
    Parts: throttle(AutoFillFieldActions.autoFillPartFields, THROTTLE_INTERVAL),
    Transcriptions: throttle(AutoFillFieldActions.autofillTranscriptions, THROTTLE_INTERVAL),
    Translations: throttle(AutoFillFieldActions.autofillTranslations, THROTTLE_INTERVAL),
  };

function getAutofillAction(name: AutofillNames): (sequenceId: string) => Promise<Promise<void>> {
  return THROTTLED_AUTOFILL_ACTIONS[name];
}

export const AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS: Array<AutofillActions> =
  AUTOFILL_COMPATIBLE_BIOENTITY_OPTION_DEFINITIONS.map((option) => ({
    ...option,
    autofillActionFn: getAutofillAction(option.name),
  }));

export interface AutoFillButtonProps {
  isEditing: boolean;
  isOligo: boolean;
  fileId: string;
  areTagsEditable: boolean;
  tagSchema: TagSchema | null;
}

/**
 * Button prompting to run autofill on parts, translations, or transcriptions, as applicable.
 *
 * If none are applicable, this renders to nothing. Otherwise, this is a button dropdown letting the user pick which
 * action to take.
 */
export default function AutoFillButton({
  isEditing,
  isOligo,
  fileId,
  areTagsEditable,
  tagSchema,
}: AutoFillButtonProps): JSX.Element | null {
  // Only render if this is a non-oligo sequence with an editable bioentity link field (part/translation/transcription).
  // Start with some basic checks before checking bioentity links.
  if (!areTagsEditable || !tagSchema || isOligo || isEditing) {
    return null;
  }
  const autofillOptions = AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS.filter((autofillOption) =>
    tagSchema.fields.some((f) => f.fieldType === autofillOption.fieldType)
  );
  if (autofillOptions.length === 0) {
    return null;
  }
  return (
    <Tooltip text="Apply autofill to visualize schema field links on sequence maps">
      <AutofillInput autofillOptions={autofillOptions} fileId={fileId} />
    </Tooltip>
  );
}
